import React, { useState, useEffect } from 'react';
import { Brain, Clock, Filter, X, ChevronDown, ChevronUp, TrendingUp, AlertTriangle } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import AIRecommendationBadge from '../components/AIRecommendationBadge';
import api, { AIAnalysisHistoryItem } from '../services/api';

const AIHistory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<AIAnalysisHistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterSymbol, setFilterSymbol] = useState(searchParams.get('symbol') || '');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<any>(null);

  useEffect(() => {
    loadHistory();
  }, [filterSymbol]);

  useEffect(() => {
    const symbol = searchParams.get('symbol');
    if (symbol && symbol !== filterSymbol) {
      setFilterSymbol(symbol);
    }
  }, [searchParams]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAIAnalysisHistory(
        filterSymbol || undefined,
        100
      );
      setHistory(data);
    } catch (err) {
      console.error('Failed to load AI analysis history:', err);
      setError('Failed to load analysis history');
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (analysisId: number) => {
    if (expandedId === analysisId) {
      setExpandedId(null);
      setExpandedDetails(null);
      return;
    }

    try {
      const details = await api.getAIAnalysisById(analysisId);
      setExpandedDetails(details);
      setExpandedId(analysisId);
    } catch (err) {
      console.error('Failed to load analysis details:', err);
    }
  };

  const handleFilterChange = (symbol: string) => {
    setFilterSymbol(symbol);
    if (symbol) {
      setSearchParams({ symbol });
    } else {
      setSearchParams({});
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const getUniqueSymbols = () => {
    const symbols = new Set(history.map((item) => item.symbol));
    return Array.from(symbols).sort();
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="text-purple-400" size={32} />
          <h1 className="text-3xl font-bold">AI Analysis History</h1>
        </div>
        <p className="text-slate-400">
          View all your past AI-powered investment analyses
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-slate-400">
            <Filter size={18} />
            <span className="text-sm font-medium">Filter by Symbol:</span>
          </div>
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              value={filterSymbol}
              onChange={(e) => handleFilterChange(e.target.value.toUpperCase())}
              placeholder="Enter symbol (e.g., AAPL, AGL.JSE)"
              className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {filterSymbol && (
              <button
                onClick={() => handleFilterChange('')}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                title="Clear filter"
              >
                <X size={18} />
              </button>
            )}
          </div>
          {history.length > 0 && (
            <div className="text-sm text-slate-400">
              {history.length} {history.length === 1 ? 'analysis' : 'analyses'}
            </div>
          )}
        </div>

        {/* Quick Symbol Filters */}
        {!filterSymbol && getUniqueSymbols().length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Quick filters:</span>
            {getUniqueSymbols().slice(0, 10).map((symbol) => (
              <button
                key={symbol}
                onClick={() => handleFilterChange(symbol)}
                className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded border border-slate-600 text-slate-300 transition-colors"
              >
                {symbol}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && history.length === 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-12 text-center">
          <Brain size={64} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No AI Analyses Yet</h3>
          <p className="text-slate-400 mb-6">
            {filterSymbol
              ? `No analyses found for ${filterSymbol}`
              : 'Start analyzing assets to see your history here'}
          </p>
          {filterSymbol && (
            <button
              onClick={() => handleFilterChange('')}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg transition-colors"
            >
              Clear Filter
            </button>
          )}
        </div>
      )}

      {/* Analysis List */}
      {history.length > 0 && (
        <div className="space-y-4">
          {history.map((item) => (
            <div
              key={item.id}
              className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden hover:border-slate-600 transition-colors"
            >
              {/* Analysis Header */}
              <div
                onClick={() => loadDetails(item.id)}
                className="p-4 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left Side */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Link
                        to={`/asset/${item.symbol}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-lg font-bold text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        {item.symbol}
                      </Link>
                      <AIRecommendationBadge
                        recommendation={item.recommendation}
                        confidence={item.confidence}
                        size="sm"
                      />
                      <span className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded capitalize">
                        {item.analysis_type}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-2 mb-2">
                      {item.summary}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock size={12} />
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                  </div>

                  {/* Right Side - Expand Icon */}
                  <div className="flex-shrink-0">
                    {expandedId === item.id ? (
                      <ChevronUp size={20} className="text-slate-400" />
                    ) : (
                      <ChevronDown size={20} className="text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === item.id && expandedDetails && (
                <div className="border-t border-slate-700 p-4 bg-slate-800/50 space-y-4">
                  {/* Full Analysis */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-300 mb-2">
                      Full Analysis
                    </h4>
                    <p className="text-sm text-slate-400 whitespace-pre-line">
                      {expandedDetails.analysis}
                    </p>
                  </div>

                  {/* Key Points */}
                  {expandedDetails.key_points && expandedDetails.key_points.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
                        <TrendingUp size={14} className="text-teal-400" />
                        Key Points
                      </h4>
                      <ul className="space-y-1">
                        {expandedDetails.key_points.map((point: string, i: number) => (
                          <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                            <span className="text-teal-400">•</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Risks */}
                  {expandedDetails.risks && expandedDetails.risks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                        <AlertTriangle size={14} />
                        Risks
                      </h4>
                      <ul className="space-y-1">
                        {expandedDetails.risks.map((risk: string, i: number) => (
                          <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                            <span className="text-red-400">•</span>
                            {risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Opportunities */}
                  {expandedDetails.opportunities && expandedDetails.opportunities.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-400 mb-2">
                        Opportunities
                      </h4>
                      <ul className="space-y-1">
                        {expandedDetails.opportunities.map((opp: string, i: number) => (
                          <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                            <span className="text-emerald-400">•</span>
                            {opp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Technical/Fundamental Outlook */}
                  {(expandedDetails.technical_outlook || expandedDetails.fundamental_outlook) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {expandedDetails.technical_outlook && (
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <h5 className="text-xs font-semibold text-slate-300 mb-2">
                            Technical Outlook
                          </h5>
                          <p className="text-xs text-slate-400">
                            {expandedDetails.technical_outlook}
                          </p>
                        </div>
                      )}
                      {expandedDetails.fundamental_outlook && (
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <h5 className="text-xs font-semibold text-slate-300 mb-2">
                            Fundamental Outlook
                          </h5>
                          <p className="text-xs text-slate-400">
                            {expandedDetails.fundamental_outlook}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Additional Context */}
                  {expandedDetails.additional_context && (
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                      <h5 className="text-xs font-semibold text-purple-400 mb-2">
                        Your Context
                      </h5>
                      <p className="text-xs text-slate-400">
                        {expandedDetails.additional_context}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIHistory;
